import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type {
  BreakdownTemplate,
  BreakdownTemplateCreateInput,
  BreakdownTemplateFromJobInput,
  BreakdownTemplateUpdateInput,
  BreakdownDocumentContents,
} from '@jobos/shared';
import { useToast } from '@/hooks/use-toast';

interface TemplatesResponse { templates: BreakdownTemplate[]; }
interface TemplateResponse { template: BreakdownTemplate; }

export const breakdownTemplateKeys = {
  all: ['breakdown-templates'] as const,
  list: () => [...breakdownTemplateKeys.all, 'list'] as const,
  detail: (id: string) => [...breakdownTemplateKeys.all, 'detail', id] as const,
};

export function useBreakdownTemplates() {
  return useQuery({
    queryKey: breakdownTemplateKeys.list(),
    queryFn: async () => (await apiClient.get<TemplatesResponse>('/breakdown-templates')).templates,
  });
}

export function useBreakdownTemplate(id: string | undefined) {
  return useQuery({
    queryKey: breakdownTemplateKeys.detail(id!),
    queryFn: async () => (await apiClient.get<TemplateResponse>(`/breakdown-templates/${id}`)).template,
    enabled: !!id,
  });
}

export function useCreateBreakdownTemplate() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (data: BreakdownTemplateCreateInput) =>
      (await apiClient.post<TemplateResponse>('/breakdown-templates', data)).template,
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: breakdownTemplateKeys.list() });
      toast({ title: 'Template created', description: `"${t.name}" created.` });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });
}

export function useCreateTemplateFromJob() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (data: BreakdownTemplateFromJobInput) =>
      (await apiClient.post<TemplateResponse>('/breakdown-templates/from-job', data)).template,
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: breakdownTemplateKeys.list() });
      toast({ title: 'Template saved', description: `"${t.name}" created from job.` });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });
}

export function useUpdateBreakdownTemplate() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: BreakdownTemplateUpdateInput }) =>
      (await apiClient.patch<TemplateResponse>(`/breakdown-templates/${id}`, data)).template,
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: breakdownTemplateKeys.list() });
      qc.invalidateQueries({ queryKey: breakdownTemplateKeys.detail(t.id) });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });
}

export function useSetDefaultTemplate() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.patch<TemplateResponse>(`/breakdown-templates/${id}/set-default`, {})).template,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: breakdownTemplateKeys.list() });
      toast({ title: 'Default template updated' });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });
}

export function useUpdateTemplateContents(id: string) {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (contents: BreakdownDocumentContents) =>
      (await apiClient.put<TemplateResponse>(`/breakdown-templates/${id}/contents`, contents)).template,
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: breakdownTemplateKeys.list() });
      qc.invalidateQueries({ queryKey: breakdownTemplateKeys.detail(t.id) });
      toast({ title: 'Template saved' });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });
}

export function useDeleteBreakdownTemplate() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (id: string) => { await apiClient.delete(`/breakdown-templates/${id}`); return id; },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: breakdownTemplateKeys.list() });
      toast({ title: 'Template deleted' });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });
}
